import React from 'react';
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from 'react-redux';
import { useState } from 'react'
import Button from '@mui/material/Button';
import IconButton from '@material-ui/core/IconButton';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PersonIcon from '@mui/icons-material/Person';
import SmartphoneIcon from '@mui/icons-material/Smartphone';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import GoogleIcon from '@mui/icons-material/Google';
import MicrosoftIcon from '@mui/icons-material/Microsoft';
import GreenvironmentLogo from '../../../src/Images/GreenvironmentLogo.svg'
import { acceptInvite, postLogin } from '../../Redux/actions';
import { authGradientButtonSx } from '../../theme/formStyles';
import AuthHero from './AuthHero';
import './Login.css';

export default function Login() {

  let navigate = useNavigate();
  const dispatch = useDispatch();

  const initForm = {
    username: '',
    password: '',
  };
  const initErr = {};
  const [form, setForm] = useState(initForm);
  const [errors, setErrors] = useState(initErr);
  const [showPassword, setShowPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('password');

  // SSO is only wired up for instances whose IdP config exists on the backend
  // (currently Amazon Federate + a couple of OneLogin staging domains).
  const ssoLoginAllowedDomains = ['g2.d2.ceegees.in', 'gaws.d2.ceegees.in', 'amazon.greenvironment.co.in'];
  const showSsoTab = ssoLoginAllowedDomains.includes(window?.location?.host);

  const emailValidator = (email) => {
    const pattern = new RegExp(/^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/i);
    return pattern.test(email);
  }

  const validateData = () => {
    let hasError = false;
    const err = Object.assign({}, errors);
    if (!emailValidator(form.username)) {
      hasError = true;
      err.username = 'Enter a valid email.';
    }
    Object.keys(form).forEach((key) => {
      if (typeof (form[key]) === 'string' && key !== 'password' && key !== 'confirm') {
        if (!form[key].match(/\w/)) {
          hasError = true;
          err[key] = 'This field is mandatory';
        }
      }
      if (!form[key]) {
        hasError = true;
        err[key] = 'This field is mandatory';
      }
    });
    if (hasError) {
      setErrors(err);
      return false;
    }
    return form;
  };

  const handleChange = (e) => {
    const { value, name } = e.target;
    const fieldValue = Object.assign({}, form);
    const errorField = Object.assign({}, errors);
    if (errorField[name]) {
      errorField[name] = null;
    }
    if (errorField['loginErr']) {
      errorField['loginErr'] = null;
    }
    setErrors(errorField);
    fieldValue[name] = value;
    if (name === 'username') {
      fieldValue[name] = value.toLowerCase();
    }
    setForm(fieldValue);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const valid = validateData();
    if (valid) {
      dispatch(postLogin(valid)).then((resp) => {
        const res = resp && resp.data;
        const inviteToken = localStorage.getItem('inviteToken')
        const err = {
          loginErr: null
        }
        setErrors(err);
        if (res.success) {
          if (inviteToken) {
            const inviteForm = {
              token: inviteToken,
              emailId: form.username
            }
            dispatch(acceptInvite(inviteForm));
            localStorage.removeItem('inviteToken')
          }
          localStorage.setItem('accessToken', res.access_token);
          navigate('/dashboard')
          window.location.reload();
        }
        else {
          const err = {
            loginErr: res.message
          }
          setErrors(err);
        }
      });
    }
  };

  if (localStorage.getItem('accessToken') && localStorage.getItem('accessToken') !== 'undefined') {
    navigate('/dashboard');
    window.location.reload();
    return null;
  }

  return (
    <div className="gv-login-page">
      <AuthHero />

      {/* ---------- Right sign-in panel ---------- */}
      <div className="gv-login-panel">
        <div className="gv-login-card">
          <div className="gv-login-card-logo">
            <img alt="Greenvironment" src={GreenvironmentLogo} />
          </div>
          <div className="gv-login-heading">Welcome back</div>
          <div className="gv-login-subheading">Sign in to access your AI Dashboard</div>

          <div className="gv-login-tabs">
            <button
              type="button"
              className={`gv-login-tab${activeTab === 'password' ? ' active' : ''}`}
              onClick={() => setActiveTab('password')}
            >
              <LockOutlinedIcon fontSize="inherit" /> Password
            </button>
            <button type="button" className="gv-login-tab disabled" disabled title="Coming soon">
              <SmartphoneIcon fontSize="inherit" /> OTP
            </button>
            {showSsoTab ? (
              <button
                type="button"
                className={`gv-login-tab${activeTab === 'sso' ? ' active' : ''}`}
                onClick={() => setActiveTab('sso')}
              >
                <FingerprintIcon fontSize="inherit" /> SSO
              </button>
            ) : (
              <button type="button" className="gv-login-tab disabled" disabled title="Coming soon">
                <FingerprintIcon fontSize="inherit" /> SSO
              </button>
            )}
          </div>

          {activeTab === 'sso' ? (
            <div className="gv-login-sso-panel">
              <div className="gv-login-subheading" style={{ marginBottom: 20 }}>
                Sign in using your organization&rsquo;s single sign-on
              </div>
              <Button
                fullWidth
                variant="contained"
                className="gv-login-btn"
                sx={authGradientButtonSx}
                href="/api/v1/auth/sso-login"
              >
                <FingerprintIcon fontSize="small" style={{ marginRight: 8 }} /> Login Using SSO
              </Button>
            </div>
          ) : (
          <>
          <form noValidate onSubmit={handleSubmit}>
            <div className="gv-login-field-row">
              <label className="gv-login-label" htmlFor="username">Email or Username</label>
            </div>
            <div className="gv-login-input-wrap">
              <span className="gv-login-input-ico"><PersonIcon fontSize="small" /></span>
              <input
                id="username"
                name="username"
                autoFocus
                placeholder="Enter email or username"
                value={form.username}
                onChange={handleChange}
                className="gv-login-input"
              />
            </div>
            {errors.username && <div className="gv-login-error">{errors.username}</div>}

            <div className="gv-login-field-row">
              <label className="gv-login-label" htmlFor="password">Password</label>
              <Link className="gv-login-forgot-link" to="/forgot-password">Forgot password?</Link>
            </div>
            <div className="gv-login-input-wrap">
              <span className="gv-login-input-ico"><LockOutlinedIcon fontSize="small" /></span>
              <input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                value={form.password}
                onChange={handleChange}
                placeholder="Enter your password"
                className="gv-login-input"
                style={{ paddingRight: 40 }}
              />
              <IconButton size="small" className="gv-login-pw-toggle" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
              </IconButton>
            </div>
            {(errors.loginErr || errors.password) && (
              <div className="gv-login-error">{errors.loginErr ? errors.loginErr : errors.password}</div>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              className="gv-login-btn"
              sx={authGradientButtonSx}
            >
              Sign In &rarr;
            </Button>
          </form>

          <div className="gv-login-or-divider"><span>or continue with</span></div>

          <div className="gv-login-social-row">
            <button type="button" className="gv-login-social-btn disabled" disabled title="Coming soon">
              <GoogleIcon fontSize="small" /> Sign in with Google
            </button>
            <button type="button" className="gv-login-social-btn disabled" disabled title="Coming soon">
              <MicrosoftIcon fontSize="small" /> Sign in with Microsoft
            </button>
          </div>

          <div className="gv-login-otp-box disabled" title="Coming soon">
            <div className="gv-login-otp-box-head">
              <SmartphoneIcon fontSize="small" />
              Login with OTP <span className="gv-login-otp-badge">NEW</span>
            </div>
            <div className="gv-login-otp-box-sub">We&rsquo;ll send a one-time code to your mobile number</div>
          </div>
          </>
          )}

          <div className="gv-login-footer-link">
            Don&rsquo;t have an account? <span>Contact Administrator</span>
          </div>
        </div>
      </div>
    </div>
  );
}
